// suggestions-panel — student-facing teacher code-suggestion review. Rebuilds the phase-3
// UI (798ef2e) that was dropped when the student surfaces were migrated: the server side
// (lib/util/lineDiff.js + the listSuggestions/accept/dismiss endpoints + the editor-api
// adapters) all survived, so this is a pure client re-wire.
//
// On a student editor surface (assignment OR challenge), initSuggestions() fetches the
// working project's PENDING suggestions and renders each as a side-by-side diff (the
// student's current code | the teacher's suggested code) with Accept & apply / Dismiss and
// a pager when there is more than one. The server computes the aligned diff (diffRows), so
// this only renders it + wires the two endpoints. No pending suggestions — or a fetch error
// — renders nothing and leaves the editor untouched.
//
//   initSuggestions({ projectId, doc }) — projectId is the WORKING project id
//   (data-draft-project-id); doc defaults to the global document.

import { fetchSuggestions, acceptSuggestion, dismissSuggestion } from './editor-api.js';

var STYLE_ID = 'pc-sugg-styles';

export function initSuggestions(opts) {
  opts = opts || {};
  var doc = opts.doc || document;
  var projectId = opts.projectId;
  if (!projectId) { return; }

  fetchSuggestions(projectId)
    .then(function (res) {
      var all = (res && res.data) || [];
      var pending = all.filter(function (s) { return (s.status || 'pending') === 'pending'; });
      if (pending.length) { mount(doc, projectId, pending); }
    })
    .catch(function () { /* silent — a suggestions hiccup must never break the editor */ });
}

function mount(doc, projectId, pending) {
  injectStyles(doc);

  // Insert a full-width banner at the very top of the surface content (above the Run bar),
  // so a waiting suggestion is impossible to miss on either surface.
  var anchor = doc.querySelector('.runner-label') || doc.querySelector('.editor-pane') || null;
  var panel = doc.createElement('section');
  panel.className = 'pc-sugg';
  panel.setAttribute('aria-label', 'Code suggestion from your teacher');
  if (anchor && anchor.parentNode) { anchor.parentNode.insertBefore(panel, anchor); }
  else { doc.body.insertBefore(panel, doc.body.firstChild); }

  var idx = 0;
  var busy = false;

  function render() {
    if (!pending.length) {
      if (panel.parentNode) { panel.parentNode.removeChild(panel); }
      return;
    }
    if (idx >= pending.length) { idx = pending.length - 1; }
    if (idx < 0) { idx = 0; }
    var s = pending[idx];

    var pager = pending.length > 1
      ? '<span class="pc-sugg-pager">' +
          '<button type="button" class="pc-sugg-nav" data-nav="prev"' + (idx === 0 ? ' disabled' : '') + ' aria-label="Previous suggestion">‹</button>' +
          '<span class="pc-sugg-count">' + (idx + 1) + ' of ' + pending.length + '</span>' +
          '<button type="button" class="pc-sugg-nav" data-nav="next"' + (idx === pending.length - 1 ? ' disabled' : '') + ' aria-label="Next suggestion">›</button>' +
        '</span>'
      : '';

    panel.innerHTML =
      '<div class="pc-sugg-head">' +
        '<span class="pc-sugg-badge">💡 Suggestion from your teacher</span>' + pager +
      '</div>' +
      (s.note ? '<p class="pc-sugg-note">' + escapeHtml(s.note) + '</p>' : '') +
      '<div class="pc-sugg-diff">' +
        '<div class="pc-sugg-diffhead"><span>Your code</span><span>Teacher’s suggestion</span></div>' +
        '<div class="pc-sugg-rows">' + diffRowsHtml(s.diffRows || []) + '</div>' +
      '</div>' +
      '<div class="pc-sugg-actions">' +
        '<span class="pc-sugg-status" role="status"></span>' +
        '<button type="button" class="pc-sugg-btn pc-sugg-dismiss" data-act="dismiss">Dismiss</button>' +
        '<button type="button" class="pc-sugg-btn pc-sugg-accept" data-act="accept">Accept &amp; apply</button>' +
      '</div>';

    var statusEl = panel.querySelector('.pc-sugg-status');
    function setStatus(t, err) {
      if (!statusEl) { return; }
      statusEl.textContent = t || '';
      statusEl.classList.toggle('pc-sugg-err', !!err);
    }
    function setBusy(on) {
      busy = on;
      Array.prototype.forEach.call(panel.querySelectorAll('button'), function (b) { b.disabled = on; });
    }

    Array.prototype.forEach.call(panel.querySelectorAll('[data-nav]'), function (b) {
      b.addEventListener('click', function () {
        if (busy) { return; }
        idx += (b.getAttribute('data-nav') === 'next' ? 1 : -1);
        render();
      });
    });

    panel.querySelector('[data-act="accept"]').addEventListener('click', function () {
      if (busy) { return; }
      setBusy(true); setStatus('Applying…');
      acceptSuggestion(projectId, s.id)
        .then(function () { setStatus('Applied — reloading…'); window.location.reload(); })
        .catch(function (e) { setBusy(false); setStatus('Could not apply — try again', true); console.error(e); });
    });

    panel.querySelector('[data-act="dismiss"]').addEventListener('click', function () {
      if (busy) { return; }
      setBusy(true); setStatus('Dismissing…');
      dismissSuggestion(projectId, s.id)
        .then(function () { pending.splice(idx, 1); busy = false; render(); })
        .catch(function (e) { setBusy(false); setStatus('Could not dismiss — try again', true); console.error(e); });
    });
  }

  render();
}

// One diff row: { left, right, type }. null side = a blank filler (keeps columns level).
function diffRowsHtml(rows) {
  return rows.map(function (r) {
    var type = r.type || 'same';
    var l = r.left == null ? '' : escapeHtml(r.left);
    var rt = r.right == null ? '' : escapeHtml(r.right);
    var lFill = r.left == null ? ' pc-sugg-fill' : '';
    var rFill = r.right == null ? ' pc-sugg-fill' : '';
    return '<div class="pc-sugg-row pc-sugg-' + type + '">' +
      '<code class="pc-sugg-l' + lFill + '">' + (l || '​') + '</code>' +
      '<code class="pc-sugg-r' + rFill + '">' + (rt || '​') + '</code>' +
    '</div>';
  }).join('');
}

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function injectStyles(doc) {
  if (doc.getElementById(STYLE_ID)) { return; }
  var st = doc.createElement('style');
  st.id = STYLE_ID;
  // Design-system tokens only (the isolated editor surfaces define them). Diff highlights
  // reuse the semantic soft status tokens, so they track the theme automatically.
  st.textContent = [
    '.pc-sugg{margin:0;padding:var(--space-4);background:var(--color-surface-2);',
    '  border-bottom:1px solid var(--color-border);border-top:3px solid var(--color-primary);}',
    '.pc-sugg *{box-sizing:border-box;}',
    '.pc-sugg-head{display:flex;align-items:center;gap:var(--space-3);flex-wrap:wrap;margin-bottom:var(--space-2);}',
    '.pc-sugg-badge{font-weight:700;font-size:var(--fs-sm);color:var(--text-strong);}',
    '.pc-sugg-pager{margin-left:auto;display:inline-flex;align-items:center;gap:var(--space-2);}',
    '.pc-sugg-count{font-size:var(--fs-xs);color:var(--text-muted);font-variant-numeric:tabular-nums;}',
    '.pc-sugg-nav{border:1px solid var(--color-border);background:var(--color-surface);',
    '  border-radius:var(--radius-md);width:26px;height:26px;cursor:pointer;font-size:16px;line-height:1;color:var(--text-body);}',
    '.pc-sugg-nav:disabled{opacity:.4;cursor:default;}',
    '.pc-sugg-note{margin:0 0 var(--space-3);font-size:var(--fs-sm);color:var(--text-body);white-space:pre-wrap;}',
    '.pc-sugg-diff{border:1px solid var(--color-border);border-radius:var(--radius-md);overflow:hidden;background:var(--color-surface);}',
    '.pc-sugg-diffhead{display:grid;grid-template-columns:1fr 1fr;gap:1px;background:var(--color-border);',
    '  font-size:var(--fs-xs);font-weight:700;text-transform:uppercase;letter-spacing:.04em;color:var(--text-muted);}',
    '.pc-sugg-diffhead span{background:var(--color-surface-2);padding:var(--space-2) var(--space-3);}',
    '.pc-sugg-rows{max-height:min(46vh,420px);overflow:auto;}',
    '.pc-sugg-row{display:grid;grid-template-columns:1fr 1fr;gap:1px;background:var(--color-border);}',
    '.pc-sugg-row code{display:block;padding:1px var(--space-3);font-family:var(--font-mono);',
    '  font-size:var(--fs-xs);line-height:1.55;white-space:pre;overflow-x:auto;background:var(--color-surface);color:var(--text-strong);}',
    '.pc-sugg-remove .pc-sugg-l,.pc-sugg-change .pc-sugg-l{background:var(--color-danger-soft);}',
    '.pc-sugg-add .pc-sugg-r,.pc-sugg-change .pc-sugg-r{background:var(--color-success-soft);}',
    '.pc-sugg-fill{background:var(--color-bg-sunken)!important;}',
    '.pc-sugg-actions{display:flex;align-items:center;gap:var(--space-3);margin-top:var(--space-3);}',
    '.pc-sugg-status{font-size:var(--fs-xs);color:var(--text-muted);}',
    '.pc-sugg-status.pc-sugg-err{color:var(--color-danger);}',
    '.pc-sugg-btn{border-radius:var(--radius-pill);padding:7px var(--space-4);font-weight:700;font-size:var(--fs-sm);cursor:pointer;border:1px solid transparent;}',
    '.pc-sugg-dismiss{margin-left:auto;background:var(--color-surface);color:var(--text-body);border-color:var(--color-border);}',
    '.pc-sugg-dismiss:hover:not(:disabled){background:var(--color-bg-sunken);}',
    '.pc-sugg-accept{background:var(--color-primary);color:var(--text-on-brand);}',
    '.pc-sugg-accept:hover:not(:disabled){background:var(--color-primary-hover);}',
    '.pc-sugg-btn:disabled{opacity:.55;cursor:default;}'
  ].join('\n');
  (doc.head || doc.body).appendChild(st);
}
